"use client";

import { Button } from "@kyan/ui";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { useComboState } from "#/store/combo/comboState";
import { useComboType } from "#/store/combo/comboType";
import { type TradeDirection } from "#/types/trade";

import { useComboGreeksTableController } from "../../ComboGreeksTable.controller";

export const AutoHedge: React.FC = () => {
  const { comboState } = useComboState();
  const { isComboOrderBook } = useComboType();
  const { delta } = useComboGreeksTableController();
  const { marketIndexPrice } = useMarketIndexPrice();
  const {
    comboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLeg,
    handleChange,
    buildInstrument,
  } = useComboInstrumentsController();

  const isEnabled =
    isComboOrderBook &&
    marketIndexPrice > 0 &&
    comboOptionInstruments.length > 0 &&
    comboOptionInstruments.every((item) => item.size);

  const handleAutoHedge = () => {
    // Calculate the required perp size to neutralize strategy delta
    // Delta is already calculated for option legs
    // We need perp delta to be -delta (opposite sign)
    const requiredPerpDelta = -delta;

    const size = Math.abs(requiredPerpDelta).toFixed(3);

    // Determine the direction: if requiredPerpDelta is negative, we need to sell
    const side: TradeDirection = requiredPerpDelta < 0 ? "sell" : "buy";

    const [existingPerp] = comboPerpInstruments;

    if (existingPerp) {
      const perpIndex = comboInstruments.findIndex(
        (instrument) => instrument.id === existingPerp.id,
      );

      handleChange(perpIndex, "size", size);
      handleChange(perpIndex, "side", side);
      return;
    }

    handleAddLeg(
      buildInstrument({
        type: "perps",
        size,
        side,
      }),
    );
  };

  if (comboState === "prices") return null;

  return (
    <Button
      size="sm"
      disabled={!isEnabled}
      variant="alternative"
      className="text-xxs hover:border-primary absolute top-0 right-6 bottom-0 my-auto h-5 min-h-5 border border-transparent px-1.5 font-semibold dark:bg-[#282828]"
      onClick={handleAutoHedge}
    >
      Auto Hedge
    </Button>
  );
};
